import { BASE_DOWNLOAD_DATA_LIMIT } from '@/storybook/components/DownloadDataButton/parts/translateCubeQueryToSQL';
import { CubeQuery } from '@/types/cube';

export const HCP_CUBE_NAME = 'hcp';
export const HCP_EMAIL_BLOCKED = 'hcp_email_blocked';
export const HCP_SCIENTIFIC_ACTIVITY_SCORE = 'hcp_scientific_activity_score';
export const HCP_SCIENTIFIC_ACTIVITY_AUTHOR = 'hcp_scientific_activity_author';
export const HCP_SCIENTIFIC_ACTIVITY_INVESTIGATOR = 'hcp_scientific_activity_investigator';
export const HCP_SCIENTIFIC_ACTIVITY_SOCIAL_MEDIA = 'hcp_scientific_activity_social_media';
export const HCP_SCIENTIFIC_ACTIVITY_GUIDELINE = 'hcp_scientific_activity_guideline';
export const CLASSIFICATION_CUBE_NAME = 'classification';
export const CLASSIFICATION_HCP_VIEW_CUBE_NAME = 'classification_hcp_view';
export const HCP_TERRITORY_CUBE_NAME = 'hcp_territory';
export const INTERACTION_CUBE_NAME = 'interaction_individual_product_attendee';
export const PARTNERSCORE_CUBE_NAME = 'partnerscore';
export const HCP_SEGMENTATION_CUBE_NAME = 'hcp_segmentation';
export const HCP_CONSENT_OVERVIEW_CUBE_NAME = 'hcp_consent_overview';
export const HCP_CONSENT_OVERVIEW_AGGREGATE_CUBE_NAME = 'hcp_consent_channel_topic_overview_aggregated';
export const HCP_CONSENT_CHANNEL_TOPIC_OVERVIEW_CUBE_NAME = 'hcp_consent_channel_topic_overview';
export const VOC_CUBE = 'voc_customer_feedback';
export const HCP_SCIENTIFIC_ACTIVITY_TOPIC_CUBE_NAME = 'hcp_scientific_activity_topic';
export const HCP_WO_CONSENT_CUBE_NAME = 'hcp_wo_consent';
export const HCP_SCIENTIFIC_ACTIVITY_SPONSOR_CUBE_NAME = 'hcp_scientific_activity_sponsor';
export const HCP_WO_INTERACTION_CUBE_NAME = 'hcp_wo_interaction';
export const CLINICAL_TRIALS_CUBE_NAME = 'clinical_trials';
export const INTERACTION_FREQUENCY_DISTRIBUTION = 'interaction_frequency_distribution';
export const HCP_PRODUCT_RATING_ADOPTION_HIST = 'hcp_product_rating_adoption_hist';
export const AREA_INTEREST_CUBE_NAME = 'hcp_area_interest';
export const HCP_CONGRESS_ABSTRACT_CUBE_NAME = 'conference_abstract';
export const HCP_CONGRESS_ABSTRACT_ONE_PAGER_CUBE_NAME = 'conference_abstract_one_pager';
export const CONGRESS_DETAIL_CUBE_NAME = 'congress_detail';
export const HCP_CONTRACTS_CUBE_NAME = 'contracts';
export const HCP_CLINICAL_TRIALS_CUBE_NAME = 'clinical_trials';
export const HCP_PUBMED_CUBE_NAME = 'pubmed';
export const HCP_SPECIALTY_SECONDARY = 'hcp_specialty_secondary';
export const HCP_SPECIALTY_OTHER = 'hcp_specialty_other';
export const PERSONIFY_HCP_RECOMMENDATION_CUBE_NAME = 'personify_hcp_recommendation';
export const PERSONIFY_HCP_RECOMMENDATION_HIST_CUBE_NAME = 'personify_hcp_recommendation_hist';
export const CUBE_INTERACTION_AGGREGATE_PRODUCT_MESSAGE = 'interaction_aggregate_product_message';
export const INSIGHT_CUBE_NAME = 'insight';

export const sortFieldMapping: Record<string, string> = {
  name: `${HCP_CUBE_NAME}.hcp_full_name_backward`,
  hco: `${HCP_CUBE_NAME}.hco_city`,
  type: `${HCP_CUBE_NAME}.type`,
  accountPlan: `${HCP_CUBE_NAME}.account_plan_in_progress_count`,
  classification: `${HCP_CUBE_NAME}.classification`,
  tae: `${HCP_CUBE_NAME}.tae_json`,
  latestInteraction: `${HCP_CUBE_NAME}.latest_interaction`,
  primaryLocation: 'primary_location.location_name',
  crm_link: `${HCP_CUBE_NAME}.crm_link`,
  hco_crm_link: `${HCP_CUBE_NAME}.hco_crm_link`,
  title: `${HCP_CUBE_NAME}.title`,
};

const getHcpBaseDimensions = (prefix: string): string[] => [
  `${prefix}.hcp_bk`,
  `${prefix}.uci`,
  `${prefix}.hcp_full_name_backward`,
  `${prefix}.type`,
  `${prefix}.classification`,
  `${prefix}.country_name`,
  `${prefix}.specialty`,
  `${prefix}.title`,
  `${prefix}.email_address_hcp_view`,
  `${prefix}.phone_number_hcp_view`,
  `${prefix}.linkedin_url`,
  `${prefix}.national_id`,
  `${prefix}.hcp_consent_profiling_business_relation_flag`,
  `${prefix}.hcp_consent_profiling_communication_flag`,
  `${prefix}.consent_disclosure`,
  `${prefix}.hcp_mdm_id`,
  `${prefix}.crm_link`,
  `${prefix}.primary_point_of_contact`,
  `${prefix}.primary_point_of_contact_email`,
  `${prefix}.hcp_registration_complete_flag`,
];

export const HCP_QUERY_TO_DOWNLOAD: CubeQuery = {
  dimensions: [
    `${HCP_CUBE_NAME}.hcp_mdm_id`,
    `${HCP_CUBE_NAME}.hcp_full_name_backward`,
    `${HCP_CUBE_NAME}.first_name`,
    'primary_location.location_name',
    `${HCP_CUBE_NAME}.type`,
    `${HCP_CUBE_NAME}.classification_json`,
    `${HCP_CUBE_NAME}.tae_json`,
    `${HCP_CUBE_NAME}.latest_interaction`,
  ],
  limit: BASE_DOWNLOAD_DATA_LIMIT,
};

export const HCP_QUERY: CubeQuery = {
  dimensions: [
    ...getHcpBaseDimensions(HCP_CUBE_NAME),
    `${HCP_CUBE_NAME}.latest_interaction`,
    `${HCP_CUBE_NAME}.hco_crm_link`,
    `${HCP_CUBE_NAME}.account_plan_in_progress_count`,
    'primary_location.location_name',
    'primary_location.city',
    'primary_location.country_name',
    'primary_location.hco_address_geolocation_latitude',
    'primary_location.hco_address_geolocation_longitude',
    `${HCP_CUBE_NAME}.country_code`,
    `${HCP_CUBE_NAME}.hcp_consent_profiling_business_relation_flag`,
    `${HCP_CUBE_NAME}.hcp_consent_profiling_communication_flag`,
    `${HCP_CUBE_NAME}.hcp_mdm_id`,
    `${HCP_CUBE_NAME}.consent_disclosure`,
    `${HCP_CUBE_NAME}.tae_json`,
    `${HCP_CUBE_NAME}.classification_json`,
  ],
  limit: 100,
};

const HCP_VIEW_NAME = 'hcp_view';

export const HCP_VIEW_QUERY: CubeQuery = {
  dimensions: [
    ...getHcpBaseDimensions(HCP_VIEW_NAME),
    `${HCP_VIEW_NAME}.age`,
    `${HCP_VIEW_NAME}.job_title`,
    `${HCP_VIEW_NAME}.title`,
    `${HCP_VIEW_NAME}.crm_link`,
    `${HCP_VIEW_NAME}.country_name`,
    `${HCP_VIEW_NAME}.consent_permission_email`,
    `${HCP_VIEW_NAME}.consent_permission_fax`,
    `${HCP_VIEW_NAME}.consent_permission_call`,
    `${HCP_VIEW_NAME}.consent_permission_sms`,
    `${HCP_VIEW_NAME}.consent_email_consent_status`,
    `${HCP_VIEW_NAME}.consent_call_consent_status`,
    `${HCP_VIEW_NAME}.consent_sms_consent_status`,
    `${HCP_VIEW_NAME}.consent_fax_consent_status`,
    `${HCP_VIEW_NAME}.consent_do_not_call`,
    `${HCP_VIEW_NAME}.consent_do_not_fax`,
    `${HCP_VIEW_NAME}.consent_do_not_mail`,
    `${HCP_VIEW_NAME}.consent_do_not_visit`,
    `${HCP_VIEW_NAME}.consent_do_not_email`,
    `${HCP_VIEW_NAME}.consent_do_not_phone`,
    `${HCP_VIEW_NAME}.consent_do_not_remote_call`,
  ],
  limit: 100,
};
